import moment from "moment";
import { ModelBase } from "../../abstract/model-base.model";
import { Currency } from "../../core/currency.model";
import { DiscountType } from "../../sales/discount-type.model";
import { InvoiceOrigin } from "../invoice-origin.model";
import { InvoiceType } from "../invoice-type.model";
import { Establishment } from "../../partner/establishment.model";
import { Partner } from "../../partner/partner.model";
import { PartnerResolutionStrategy } from "../partner-resolution-strategy.model";
import { Shareholder } from "../../client/shareholder.model";
import { FkField, OmitField } from "src/app/@shared/decorators/api-field.decorator";

import { StatusEnum } from "src/app/@shared/enums/status.enum";
import { InvoiceTypeEnum } from "src/app/@shared/enums/invoice-type.enum";
import { InvoiceProcessingStatus, InvoiceProcessingStatusConfig, InvoiceProcessingStatusOption, IN_PROGRESS_STATUSES } from "src/app/@shared/enums/invoice-processing-status.enum";
import { InvoiceOriginEnum, InvoiceOriginOption } from "src/app/@shared/enums/invoice-origin.enum";
import { InvoiceOcrResult } from "../invoice-ocr-result.interface";
import { OcrPayload, QualifiedPayload } from "../invoice-payloads.interface";
import { tagAccents, aiTag } from 'src/assets/themes/biifor/components/tag';
// Types only — élidés à la compilation, pas de dépendance runtime vers le composant.
import type { MessageAction, MessageSeverity } from "src/app/@shared/components/message/message.component";


/** Config de la bannière de statut pipeline (rendu via <app-message>). */
export interface ProcessingStatusBarConfig {
    visible: boolean;
    severity: MessageSeverity;
    icon: string;
    title: string;
    messages?: string[];
    primaryAction?: MessageAction;
    secondaryActions?: MessageAction[];
}

/** Handlers + états de chargement injectés par la page de détail dans la bannière. */
export interface ProcessingStatusBarActions {
    /**
     * Droit de créer la facture : relancer l'analyse et remplacer la pièce finissent le
     * dépôt, le serveur les soumet à ce droit plutôt qu'à la modification (#1218)
     */
    canCreate: boolean;
    /** Activité `update` de la facture : compléter manuellement, confirmer, abandonner (#1218) */
    canUpdate: boolean;
    onRetry: () => void;
    onAbandon: () => void;
    onConfirmIsInvoice: () => void;
    onReplaceFile: () => void;
    onDelete: () => void;
    actionLoading: boolean;
    replaceFileLoading: boolean;
}

export abstract class InvoiceBase extends ModelBase {

    private _aiFilledFields!: string[];
    private _amount!: number;
    private _amountDue!: number;
    private _amountDueOrg!: number;
    private _comment!: string;
    private _currency!: Currency;
    private _currencyOrg!: Currency;
    private _discount!: number;
    private _discountType!: DiscountType;
    private _dueDate!: moment.Moment;
    private _exchangeRate!: number;
    private _forcedAsInvoice!: boolean;
    private _grossAmount!: number;
    private _grossAmountOrg!: number;
    private _headerOnly!: boolean;
    private _isExpense!: boolean;
    private _issueDate!: moment.Moment;
    private _label!: string;
    private _manageTax!: boolean;
    private _manualExchangeRate!: boolean;
    private _netAmount!: number;
    private _netAmountOrg!: number;
    private _ocrPayload!: OcrPayload | null;
    private _ocrResult!: InvoiceOcrResult;
    private _origin!: InvoiceOrigin;
    private _partner!: Partner;
    private _partnerEstablishment!: Establishment;
    private _partnerResolutionStrategy!: PartnerResolutionStrategy | null;
    private _processingStatus!: InvoiceProcessingStatus;
    private _processingStatusReason!: string | null;
    private _qualifiedPayload!: QualifiedPayload | null;
    private _reference!: string;
    private _shareholder!: Shareholder;
    private _taxAmount!: number;
    private _taxAmountOrg!: number;
    private _type!: InvoiceType;

    /**
     * Options du filtre multi-select sur l'origine (1 origine = 1 option).
     *
     * ⚠️ Les libellés sont recopiés de l'énumération serveur (`InvoiceOriginEnum`) : le
     * serveur ne publie pas la liste des origines possibles, seulement celle que porte
     * chaque document. Les garder identiques, sinon le filtre et la colonne nomment la
     * même origine de deux façons différentes.
     */
    public static originFilterOptions(): InvoiceOriginOption[] {
        return [
            { value: InvoiceOriginEnum.Api, label: "Import par API d'un logiciel tiers", icon: InvoiceBase.originIcon(InvoiceOriginEnum.Api) },
            { value: InvoiceOriginEnum.Email, label: 'Réception par email', icon: InvoiceBase.originIcon(InvoiceOriginEnum.Email) },
            { value: InvoiceOriginEnum.Import, label: 'Import fichier plat', icon: InvoiceBase.originIcon(InvoiceOriginEnum.Import) },
            { value: InvoiceOriginEnum.Internal, label: 'Création interne', icon: InvoiceBase.originIcon(InvoiceOriginEnum.Internal) },
            { value: InvoiceOriginEnum.Upload, label: 'Upload fichier PDF', icon: InvoiceBase.originIcon(InvoiceOriginEnum.Upload) },
        ];
    }

    /**
     * Icône représentant l'origine d'un document. Le libellé, lui, vient du serveur
     * (`origin.label`) : seule l'icône est portée par le front.
     */
    public static originIcon(origin: string): string {
        switch (origin) {
            case InvoiceOriginEnum.Api:
                return 'fa-solid fa-plug-circle-check';
            case InvoiceOriginEnum.Email:
                return 'fa-solid fa-envelope';
            case InvoiceOriginEnum.Import:
                return 'fa-solid fa-file-import';
            case InvoiceOriginEnum.Internal:
                return 'bi bi-biifor';
            case InvoiceOriginEnum.Upload:
                return 'fa-solid fa-file-upload';
            default:
                return 'fa-solid fa-question';
        }
    }

    /**
     * Construit la config de la bannière de statut pipeline (rendu via <app-message>).
     * Les actions (retry/abandon/replace/delete) sont injectées par la page de détail, avec
     * les droits qui décident de leur affichage.
     */
    public static processingStatusBar(invoice: InvoiceBase | null | undefined, actions: ProcessingStatusBarActions): ProcessingStatusBarConfig {
        if (!invoice) return { visible: false, severity: 'info', icon: '', title: '' };

        const status = invoice.processingStatus;
        const reason = invoice.processingStatusReason;
        const reasonMessages: string[] = reason ? reason.split('\n').filter(l => l.trim()) : [];

        switch (status) {
            // Étapes intermédiaires
            case InvoiceProcessingStatus.TechnicalValidationInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Vérification de la pièce jointe…' };
            case InvoiceProcessingStatus.RelevanceCheckInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Analyse de pertinence…' };
            case InvoiceProcessingStatus.OcrInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Lecture de la facture en cours…' };
            case InvoiceProcessingStatus.PartnerIdentificationInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Identification du tiers…' };
            case InvoiceProcessingStatus.QualificationInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Qualification des données…' };
            case InvoiceProcessingStatus.MappingInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Application des données…' };
            case InvoiceProcessingStatus.AllocationInProgress:
                return { visible: true, severity: 'ai', icon: 'fa-solid fa-arrow-rotate-right fa-spin', title: 'Proposition d\'imputation en cours…' };

            // Échecs retryables
            case InvoiceProcessingStatus.RelevanceCheckFailed:
            case InvoiceProcessingStatus.OcrFailed:
            case InvoiceProcessingStatus.PartnerIdentificationFailed:
            case InvoiceProcessingStatus.QualificationFailed:
            case InvoiceProcessingStatus.MappingFailed:
                return {
                    visible: true,
                    severity: 'error',
                    icon: 'fa-solid fa-circle-xmark',
                    title: 'Échec de l\'analyse',
                    messages: reasonMessages.length ? reasonMessages : ['L\'analyse de la facture a échoué.'],
                    primaryAction: actions.canCreate
                        ? { label: 'Réessayer', handler: actions.onRetry, loading: actions.actionLoading }
                        : undefined,
                    secondaryActions: actions.canUpdate
                        ? [{ label: 'Compléter manuellement', handler: actions.onAbandon, loading: actions.actionLoading }]
                        : [],
                };

            // Pseudo-terminaux replace-file
            case InvoiceProcessingStatus.ValidationFailed:
                return {
                    visible: true,
                    severity: 'warn',
                    icon: 'fa-solid fa-triangle-exclamation',
                    title: 'Pièce jointe invalide',
                    messages: reasonMessages.length ? reasonMessages : ['La pièce jointe ne respecte pas les contraintes techniques.'],
                    primaryAction: actions.canCreate
                        ? { label: 'Remplacer la pièce jointe', handler: actions.onReplaceFile, loading: actions.replaceFileLoading }
                        : undefined,
                    secondaryActions: [{ label: 'Supprimer la facture', handler: actions.onDelete, loading: actions.actionLoading }],
                };
            case InvoiceProcessingStatus.NotAnInvoice:
                return {
                    visible: true,
                    severity: 'warn',
                    icon: 'fa-solid fa-triangle-exclamation',
                    title: 'Document non reconnu comme facture',
                    messages: reasonMessages.length ? reasonMessages : ['Le document n\'a pas été reconnu comme une facture.'],
                    primaryAction: actions.canUpdate
                        ? { label: 'Confirmer comme facture', handler: actions.onConfirmIsInvoice, loading: actions.actionLoading }
                        : undefined,
                    secondaryActions: [
                        ...(actions.canCreate
                            ? [{ label: 'Remplacer la pièce jointe', handler: actions.onReplaceFile, loading: actions.replaceFileLoading }]
                            : []),
                        ...(actions.canUpdate
                            ? [{ label: 'Abandonner', handler: actions.onAbandon, loading: actions.actionLoading }]
                            : []),
                    ],
                };

            // Avertissement (Phase 1 : prévu mais non déclenché par le back)
            case InvoiceProcessingStatus.ConsistencyWarning: {
                const consistency = invoice.qualifiedPayload?.consistency;
                const detail: string[] = consistency
                    ? [
                        `Entête : ${consistency.headerNetAmount} | Somme des lignes : ${consistency.linesNetAmountSum} | Écart : ${consistency.delta}`,
                        'Vérifiez les valeurs. La validation est bloquée tant que l\'écart subsiste.',
                    ]
                    : ['Vérifiez les valeurs. La validation est bloquée tant que l\'écart subsiste.'];
                return {
                    visible: true,
                    severity: 'warn',
                    icon: 'fa-solid fa-triangle-exclamation',
                    title: 'Incohérence entête/lignes détectée',
                    messages: detail,
                };
            }

            // Terminaux finaux : pas de bannière
            case InvoiceProcessingStatus.Ready:
            case InvoiceProcessingStatus.Abandoned:
            case InvoiceProcessingStatus.NotApplicable:
            default:
                return { visible: false, severity: 'info', icon: '', title: '' };
        }
    }

    public static processingStatusConfig(status: InvoiceProcessingStatus): InvoiceProcessingStatusConfig {
        const map: Record<InvoiceProcessingStatus, InvoiceProcessingStatusConfig> = {
            [InvoiceProcessingStatus.TechnicalValidationInProgress]: { label: 'Vérification PJ', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.RelevanceCheckInProgress]: { label: 'Analyse pertinence', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.OcrInProgress]: { label: 'Lecture OCR', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.PartnerIdentificationInProgress]: { label: 'Identification tiers', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.QualificationInProgress]: { label: 'Qualification', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.MappingInProgress]: { label: 'Application', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.AllocationInProgress]: { label: 'Imputation IA', severity: undefined, spinning: true, aiTag: aiTag },
            [InvoiceProcessingStatus.RelevanceCheckFailed]: { label: 'Échec analyse', severity: 'danger' },
            [InvoiceProcessingStatus.OcrFailed]: { label: 'Échec OCR', severity: 'danger' },
            [InvoiceProcessingStatus.PartnerIdentificationFailed]: { label: 'Échec tiers', severity: 'danger' },
            [InvoiceProcessingStatus.QualificationFailed]: { label: 'Échec qualification', severity: 'danger' },
            [InvoiceProcessingStatus.MappingFailed]: { label: 'Échec application', severity: 'danger' },
            [InvoiceProcessingStatus.ValidationFailed]: { label: 'PJ invalide', severity: 'warn' },
            [InvoiceProcessingStatus.NotAnInvoice]: { label: 'Pas une facture', severity: 'warn' },
            [InvoiceProcessingStatus.Ready]: { label: 'Terminée', severity: 'success' },
            [InvoiceProcessingStatus.ConsistencyWarning]: { label: 'Incohérence', severity: 'warn' },
            [InvoiceProcessingStatus.Abandoned]: { label: 'Abandonnée', severity: 'secondary' },
            [InvoiceProcessingStatus.NotApplicable]: { label: '', severity: undefined },
        };
        return map[status] ?? { label: status, severity: 'secondary' };
    }

    /**
     * Liste des options de filtre sur le statut pipeline (1 statut = 1 option),
     * hors NotApplicable (statut technique sans pipeline, label vide).
     */
    public static processingStatusFilterOptions(): InvoiceProcessingStatusOption[] {
        return Object.values(InvoiceProcessingStatus)
            .filter(status => status !== InvoiceProcessingStatus.NotApplicable)
            .map(status => {
                const cfg = InvoiceBase.processingStatusConfig(status);
                return { value: status, label: cfg.label, severity: cfg.severity, spinning: cfg.spinning, aiTag: cfg.aiTag };
            });
    }

    public static ribbonColor(severity: 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined): string {
        switch (true) {
            case (severity === 'contrast'):
                return 'rgba(31, 41, 55, 1)';
            case (severity === 'danger'):
                return 'rgba(215, 49, 57, 1)';
            case (severity === 'secondary'):
                return 'rgba(108, 117, 125, 1)';
            case (severity === 'success'):
                return 'rgba(25, 176, 115, 1)';
            case (severity === 'warn'):
                return 'rgba(241, 167, 0, 1)';
            case (severity === 'info'):
            default:
                return 'rgba(0, 87, 255, 1)';
        }
    }

    public static statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (status === StatusEnum.Draft):
                return 'warn';
            case (status === StatusEnum.DraftInt):
                return 'secondary';
            case (status === StatusEnum.Cancelled):
                return 'secondary';
            case (status === StatusEnum.Paid):
            case (status === StatusEnum.PartiallyPaid):
                return 'success';
            case (status === StatusEnum.Sent):
            case (status === StatusEnum.Validated):
                return undefined;
            case (status === StatusEnum.ValidatedInt):
                return 'warn';
            default:
                return undefined;
        }
    }

    public static statusLabel(status: string): string {
        switch (status) {
            case StatusEnum.Draft:
                return 'À traiter';
            case StatusEnum.DraftInt:
                return 'Brouillon';
            case StatusEnum.Cancelled:
                return 'Annulée';
            case StatusEnum.Paid:
                return 'Payée';
            case StatusEnum.PartiallyPaid:
                return 'Part. payée';
            case StatusEnum.Validated:
                return 'Validée';
            case StatusEnum.ValidatedInt:
                return 'Finalisée';
            case StatusEnum.Sent:
                return 'Envoyée';
            case StatusEnum.Used:
                return 'Utilisé';
            default:
                return '';
        }
    }

    public static tagAccent(status: string): Object | undefined {
        switch (true) {
            case (status === StatusEnum.Cancelled):
            case (status === StatusEnum.Paid):
            case (status === StatusEnum.ValidatedInt):
                return tagAccents;
            default:
                return undefined;
        }
    }

    /**
     * Texte à afficher dans une colonne « Libellé » de liste ou de widget.
     *
     * Sur une facture d'achat ou importée, c'est le libellé saisi. Les documents de vente
     * redéfinissent cet accesseur : l'objet imprimé et le libellé comptable y sont deux
     * champs distincts depuis le ticket #941.
     */
    public get displayLabel(): string {
        return this.label;
    }

    /**
     * ⚠️ `type` n'est pas toujours hydraté : les listes restreignent les champs ramenés
     * par `$select` (la liste des devis ne demande pas `type`). Sans l'accès optionnel,
     * tout appel — y compris indirect, via `title` ou `toString()` — plante.
     */
    public get isCreditNote(): boolean {
        return this._type?.code === 'credit_note';
    }

    /**
     * Méthode pour voir si la facture est interne ou externe
     */
    public get isInternal(): boolean {
        return this.origin.code === 'internal';
    }

    public get isLate(): boolean {
        return !this.hasStatus(StatusEnum.Draft) && !this.hasStatus(StatusEnum.Paid) && this.amountDue > 0 && this.dueDate?.isBefore(moment());
    }

    /**
     * Le pipeline OCR n'est pas en cours de traitement : la ligne de liste est
     * cliquable et la facture consultable (états finaux, échecs, remplacement fichier…).
     */
    public get isPipelineClickable(): boolean {
        return !IN_PROGRESS_STATUSES.includes(this.processingStatus);
    }

    public get lateText(): string {
        return 'En retard de ' + this.dueDate.fromNow(true);
    }

    /**
     * Icône de l'origine du document, pour les listes et la fiche.
     */
    public get originIcon(): string {
        return InvoiceBase.originIcon(this._origin?.code);
    }

    /**
     * Config visuelle du statut pipeline pour rendu badge `<p-tag>`.
     */
    public get processingStatusConfig(): InvoiceProcessingStatusConfig {
        return InvoiceBase.processingStatusConfig(this.processingStatus);
    }


    /**
     * Couleur de ruban selon le statut de la facture
     */
    public get ribbonColor(): string {
        return InvoiceBase.ribbonColor(this.statusSeverity);
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public get statusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.Cancelled)):
                return InvoiceBase.statusSeverity(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Draft)):
                return this.isInternal ? InvoiceBase.statusSeverity(StatusEnum.DraftInt) : InvoiceBase.statusSeverity(StatusEnum.Draft);
            case (this.hasStatus(StatusEnum.Paid)):
                return InvoiceBase.statusSeverity(StatusEnum.Paid);
            case (this.hasStatus(StatusEnum.PartiallyPaid)):
                return InvoiceBase.statusSeverity(StatusEnum.PartiallyPaid);
            case (this.hasStatus(StatusEnum.Sent)):
                return InvoiceBase.statusSeverity(StatusEnum.Sent);
            case (this.hasStatus(StatusEnum.Validated)):
                return this.isInternal ? InvoiceBase.statusSeverity(StatusEnum.ValidatedInt) : InvoiceBase.statusSeverity(StatusEnum.Validated);
            default:
                return InvoiceBase.statusSeverity('');
        }
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public get statusLabel(): string {
        switch (true) {
            case (this.hasStatus(StatusEnum.Cancelled)):
                return InvoiceBase.statusLabel(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Draft)):
                return this.isInternal ? InvoiceBase.statusLabel(StatusEnum.DraftInt) : InvoiceBase.statusLabel(StatusEnum.Draft);
            case (this.hasStatus(StatusEnum.Paid)):
                return this.isCreditNote ? InvoiceBase.statusLabel(StatusEnum.Used) : InvoiceBase.statusLabel(StatusEnum.Paid);
            case (this.hasStatus(StatusEnum.PartiallyPaid)):
                return InvoiceBase.statusLabel(StatusEnum.PartiallyPaid);
            case (this.hasStatus(StatusEnum.Sent)):
                return InvoiceBase.statusLabel(StatusEnum.Sent);
            case (this.hasStatus(StatusEnum.Validated)):
                return this.isInternal ? InvoiceBase.statusLabel(StatusEnum.ValidatedInt) : InvoiceBase.statusLabel(StatusEnum.Validated);
            default:
                return '';
        }
    }

    public get tagAccent(): Object | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.Cancelled)):
                return InvoiceBase.tagAccent(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Draft)):
                return InvoiceBase.tagAccent(StatusEnum.Draft);
            case (this.hasStatus(StatusEnum.Paid)):
                return InvoiceBase.tagAccent(StatusEnum.Paid);
            case (this.hasStatus(StatusEnum.PartiallyPaid)):
                return InvoiceBase.tagAccent(StatusEnum.PartiallyPaid);
            case (this.hasStatus(StatusEnum.Sent)):
                return InvoiceBase.tagAccent(StatusEnum.Sent);
            case (this.hasStatus(StatusEnum.Validated)):
                return this.isInternal ? InvoiceBase.tagAccent(StatusEnum.ValidatedInt) : InvoiceBase.tagAccent(StatusEnum.Validated);
            default:
                return undefined;
        }
    }

    public get title(): string {
        let title = '';
        if (!!this.reference) {
            let invoiceTypeLabel = this.isCreditNote ? 'Avoir' : 'Facture';
            title = invoiceTypeLabel + ' ' + this.reference
        } else if (this.isCreditNote) {
            title = 'Nouvel avoir';
        } else {
            title = 'Nouvelle facture';
        }
        return title;
    }

    /**
     * Même précaution que `isCreditNote` : cette méthode est appelée sur des instances
     * de liste, dont les relations et les dates ne sont pas toutes ramenées.
     */
    public override toString(): string {
        const issueDate = this.issueDate?.isValid() ? this.issueDate.format('DD/MM/YYYY') : '';
        return `${this.partner?.name} - ${this.isCreditNote ? 'Avoir' : 'Facture'} ${this.reference} du ${issueDate} - ${this.label}`;
    }

    /**
     * Getters et setters
     */

    @OmitField()
    public get aiFilledFields(): string[] {
        return this._aiFilledFields;
    }

    public set aiFilledFields(value: any) {
        // Le middleware back camelise les clés mais pas les valeurs : les noms de
        // champs arrivent en snake_case (ex. "issue_date"). On les normalise en
        // camelCase pour matcher les noms de champs côté front (ex. "issueDate").
        const camelized = Array.isArray(value)
            ? value.map((field: any) => typeof field === 'string'
                ? field.replace(/_([a-z])/g, (_match, char) => char.toUpperCase())
                : field)
            : value;
        this._setValue('_aiFilledFields', camelized, 'string');
    }

    public abstract get allocations();

    public abstract set allocations(value: any);

    @OmitField()
    public get amount(): number {
        return this._amount;
    }

    public set amount(value: any) {
        this._setValue('_amount', value, 'float');
    }

    public get amountDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.amount;
        } else {
            return this.amount;
        }
    }

    @OmitField()
    public get amountDue(): number {
        return this._amountDue;
    }

    public set amountDue(value: any) {
        this._setValue('_amountDue', value, 'float');
    }

    public get amountDueDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.amountDue;
        } else {
            return this.amountDue;
        }
    }

    @OmitField()
    public get amountDueOrg(): number {
        return this._amountDueOrg;
    }

    public set amountDueOrg(value: any) {
        this._setValue('_amountDueOrg', value, 'float');
    }

    public get amountDueOrgDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.amountDueOrg;
        } else {
            return this.amountDueOrg;
        }
    }

    public get amountUnmatched(): number {
        return this.amountDue;
    }

    public get comment(): string {
        return this._comment;
    }

    public set comment(value: any) {
        this._setValue('_comment', value, 'string');
    }

    @FkField('code')
    public get currency(): Currency {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    @OmitField()
    public get currencyOrg(): Currency {
        return this._currencyOrg;
    }

    public set currencyOrg(value: any) {
        this._setValue('_currencyOrg', value, 'Currency');
    }

    public get discount(): number {
        return this._discount;
    }

    public set discount(value: any) {
        this._setValue('_discount', value, 'float');
    }

    @FkField('code')
    public get discountType(): DiscountType {
        return this._discountType;
    }

    public set discountType(value: any) {
        this._setValue('_discountType', value, 'DiscountType');
    }

    public get dueDate(): moment.Moment {
        return this._dueDate;
    }

    public set dueDate(value: any) {
        this._setValue('_dueDate', value, 'date');
    }

    public get exchangeRate(): number {
        return this._exchangeRate;
    }

    public set exchangeRate(value: any) {
        let newValue = parseFloat(value) * 100000 / 100000;
        if (this.loaded && newValue !== this._exchangeRate) {
            // Modification survient après que l'objet a été instancié
            this.hasChanged = true;
        }
        this._exchangeRate = newValue;
    }

    /**
     * Trace d'audit : `true` si l'utilisateur a explicitement confirmé le document
     * comme étant une facture après que l'IA l'avait jugé non pertinent (action
     * `confirm-is-invoice` depuis le statut `NOT_AN_INVOICE`). Géré côté back uniquement.
     */
    @OmitField()
    public get forcedAsInvoice(): boolean {
        return this._forcedAsInvoice;
    }

    public set forcedAsInvoice(value: any) {
        this._setValue('_forcedAsInvoice', value, 'boolean');
    }

    public get grossAmount(): number {
        return this._grossAmount;
    }

    public set grossAmount(value: any) {
        this._setValue('_grossAmount', value, 'float');
        if (this._headerOnly) {
            this.netAmount = this.grossAmount - this.taxAmount;
        }
    }

    public get grossAmountDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.grossAmount;
        } else {
            return this.grossAmount;
        }
    }

    @OmitField()
    public get grossAmountOrg(): number {
        return this._grossAmountOrg;
    }

    public set grossAmountOrg(value: any) {
        this._setValue('_grossAmountOrg', value, 'float');
    }

    public get grossAmountOrgDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.grossAmountOrg;
        } else {
            return this.grossAmountOrg;
        }
    }

    public get hasOcrResult(): boolean {
        return Object.keys(this.ocrResult).length > 0;
    }

    public get headerOnly(): boolean {
        return this._headerOnly;
    }

    public set headerOnly(value: any) {
        this._setValue('_headerOnly', value, 'boolean');
    }

    public get isExpense(): boolean {
        return this._isExpense;
    }

    public set isExpense(value: any) {
        this._setValue('_isExpense', value, 'boolean');
    }

    public get issueDate(): moment.Moment {
        return this._issueDate;
    }

    public set issueDate(value: any) {
        this._setValue('_issueDate', value, 'date');
    }

    public abstract get items();

    public abstract set items(value: any);

    public get label(): string {
        return this._label;
    }

    public set label(value: any) {
        this._setValue('_label', value, 'string');
    }

    @OmitField()
    public get manageTax(): boolean {
        return this._manageTax;
    }

    public set manageTax(value: any) {
        this._setValue('_manageTax', value, 'boolean');
    }

    public get manualExchangeRate(): boolean {
        return this._manualExchangeRate;
    }

    public set manualExchangeRate(value: any) {
        this._setValue('_manualExchangeRate', value, 'boolean');
    }

    public get netAmount(): number {
        return this._netAmount;
    }

    public set netAmount(value: any) {
        this._setValue('_netAmount', value, 'float');
    }

    @OmitField()
    public get netAmountOrg(): number {
        return this._netAmountOrg;
    }

    public set netAmountOrg(value: any) {
        this._setValue('_netAmountOrg', value, 'float');
    }

    public get netAmountDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.netAmount;
        } else {
            return this.netAmount;
        }
    }

    @OmitField()
    public get ocrPayload(): OcrPayload | null {
        return this._ocrPayload;
    }

    public set ocrPayload(value: any) {
        this._setValue('_ocrPayload', value, 'json');
    }

    @OmitField()
    public get ocrResult(): InvoiceOcrResult {
        return this._ocrResult;
    }

    public set ocrResult(value: any) {
        this._setValue('_ocrResult', value, 'json');
    }

    @FkField('code')
    public get origin(): InvoiceOrigin {
        return this._origin;
    }

    public set origin(value: any) {
        this._setValue('_origin', value, 'InvoiceOrigin');
    }

    @FkField()
    public get partner(): Partner {
        return this._partner;
    }

    public set partner(value: any) {
        this._setValue('_partner', value, 'Partner');
    }

    @FkField()
    public get partnerEstablishment(): Establishment {
        return this._partnerEstablishment;
    }

    public set partnerEstablishment(value: any) {
        this._setValue('_partnerEstablishment', value, 'Establishment');
    }

    /**
     * Règle qui a désigné le partenaire à l'étape d'identification du pipeline OCR.
     * Nulle quand aucune résolution automatique n'a eu lieu — y compris quand le
     * serveur s'est abstenu parce que plusieurs partenaires étaient plausibles.
     */
    @OmitField()
    public get partnerResolutionStrategy(): PartnerResolutionStrategy | null {
        return this._partnerResolutionStrategy;
    }

    public set partnerResolutionStrategy(value: any) {
        this._setValue('_partnerResolutionStrategy', value, 'PartnerResolutionStrategy');
    }

    @OmitField()
    public get processingStatus(): InvoiceProcessingStatus {
        return this._processingStatus;
    }

    public set processingStatus(value: any) {
        // Le back sérialise le statut en objet { code, label } (pattern AppConfigModel).
        // On ne conserve que le code pour rester aligné avec l'enum InvoiceProcessingStatus.
        const code = value && typeof value === 'object' ? value.code : value;
        this._setValue('_processingStatus', code, 'string');
    }

    @OmitField()
    public get processingStatusReason(): string | null {
        return this._processingStatusReason;
    }

    public set processingStatusReason(value: any) {
        this._setValue('_processingStatusReason', value, 'string');
    }

    @OmitField()
    public get qualifiedPayload(): QualifiedPayload | null {
        return this._qualifiedPayload;
    }

    public set qualifiedPayload(value: any) {
        this._setValue('_qualifiedPayload', value, 'json');
    }

    public get reference(): string {
        return this._reference;
    }

    public set reference(value: any) {
        this._setValue('_reference', value, 'string');
    }

    public get shareholder(): Shareholder {
        return this._shareholder;
    }

    public set shareholder(value: any) {
        this._setValue('_shareholder', value, 'Shareholder');
    }

    public abstract get taxes();

    public abstract set taxes(value: any);

    public get taxAmount(): number {
        return this._taxAmount;
    }

    public set taxAmount(value: any) {
        this._setValue('_taxAmount', value, 'float');
        if (this._headerOnly) {
            this.netAmount = this._grossAmount - this._taxAmount;
        }
    }

    @OmitField()
    public get taxAmountOrg(): number {
        return this._taxAmountOrg;
    }

    public set taxAmountOrg(value: any) {
        this._setValue('_taxAmountOrg', value, 'float');
    }

    public get taxAmountDisp(): number {
        if (this.type.code === InvoiceTypeEnum.CreditNote) {
            return -this.taxAmount;
        } else {
            return this.taxAmount;
        }
    }

    @FkField('code')
    public get type(): InvoiceType {
        return this._type;
    }

    public set type(value: any) {
        this._setValue('_type', value, 'InvoiceType');
    }

}